/**
 * Credential encryption helpers.
 *
 * The user's password is encrypted with a shared passphrase before it is
 * written to localStorage (`ssCreds`) or sent to the backend. CryptoJS's
 * `AES.encrypt(text, passphrase)` produces an OpenSSL-compatible payload
 * (`base64("Salted__" + 8-byte salt + AES-256-CBC ciphertext)`, key/IV
 * derived via the legacy EVP_BytesToKey MD5 KDF). The backend
 * (`app/utils/crypto.py`) reverses exactly this format.
 *
 * The passphrase comes from `REACT_APP_CRYPTO_SECRET` and is baked into the
 * bundle at build time. There is intentionally NO fallback default: if the
 * variable is missing we fail loudly rather than encrypt with a known key.
 */

import AES from "crypto-js/aes";
import Utf8 from "crypto-js/enc-utf8";

const SECRET = process.env.REACT_APP_CRYPTO_SECRET;

const requireSecret = () => {
  if (!SECRET) {
    // Surfaced to the user via the login error toast; logged for the developer.
    console.error(
      "REACT_APP_CRYPTO_SECRET is not set — credential encryption cannot run. " +
        "Set it in .env.local (and the Docker build arg) and rebuild.",
    );
    throw new Error("Credential encryption is not configured");
  }
  return SECRET;
};

/**
 * Encrypt a plaintext password. Empty/blank input is returned untouched so
 * callers don't have to special-case it.
 * @param {string} plainText
 * @returns {string} CryptoJS AES ciphertext (OpenSSL "Salted__" base64)
 */
export const encryptPassword = (plainText) => {
  if (plainText === undefined || plainText === null || plainText === "") {
    return plainText;
  }
  return AES.encrypt(String(plainText), requireSecret()).toString();
};

/**
 * Decrypt a password ciphertext produced by `encryptPassword`. If the value
 * is not recognisable ciphertext it is returned unchanged (e.g. legacy
 * plaintext `ssCreds` from a session that predates this change).
 * @param {string} cipherText
 * @returns {string}
 */
export const decryptPassword = (cipherText) => {
  if (!cipherText) return cipherText;
  try {
    const plain = AES.decrypt(String(cipherText), requireSecret()).toString(Utf8);
    return plain || cipherText;
  } catch (e) {
    return cipherText;
  }
};

const cryptoService = { encryptPassword, decryptPassword };
export default cryptoService;
